import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { EmploymentStatusSelector } from '@/components/Select/EmploymentStatusSelector'
import { OccupationSelector } from '@/components/Select/OccupationSelector'
import { EMPLOYMENT_STATUS, OCCUPATION } from '@/constants/user-profile'
import { FieldError, TextField } from '@/components/FormField'
import { toCanonicalOccupation, type OccupationFormValues } from './occupation'

export const OccupationFields = React.memo(function OccupationFields() {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<OccupationFormValues>()

  const employmentStatus = useWatch({ control, name: 'employmentStatus' })
  const occupation = useWatch({ control, name: 'occupation' })

  const showOccupationList =
    employmentStatus === EMPLOYMENT_STATUS.EMPLOYED ||
    employmentStatus === EMPLOYMENT_STATUS.SELF_EMPLOYED
  const showOccupationInput = occupation === OCCUPATION.OTHER

  return (
    <>
      <div
        className="text-lg font-normal uppercase mt-2"
        data-testid="container-status-label"
      >
        {t('occupation.employmentStatus.title')}
      </div>
      <Controller
        control={control}
        name="employmentStatus"
        render={({ field }) => (
          <EmploymentStatusSelector
            value={field.value}
            onChange={(newValue) => {
              field.onChange(newValue)
              setValue('occupation', '', { shouldValidate: true })
              setValue('typedOccupation', '')
              setValue('occupationDetails', '')
            }}
          />
        )}
      />
      <FieldError
        message={errors.employmentStatus?.message}
        id="occupation-employment-status-error"
      />

      {showOccupationList && (
        <div data-testid="select-section">
          <div
            className="text-base font-normal uppercase"
            data-testid="select-label"
          >
            {t('occupation.occupation.title')}
          </div>
          <Controller
            control={control}
            name="typedOccupation"
            render={({ field }) => (
              <OccupationSelector
                value={field.value}
                onChange={(newValue) => {
                  field.onChange(newValue)
                  const canonicalOccupation = toCanonicalOccupation(newValue)
                  setValue('occupation', canonicalOccupation, {
                    shouldValidate: true,
                  })
                  if (canonicalOccupation === OCCUPATION.OTHER) {
                    setValue('occupationDetails', '', { shouldValidate: true })
                  }
                }}
              />
            )}
          />
          <FieldError
            message={errors.occupation?.message}
            id="occupation-error"
          />
        </div>
      )}

      {showOccupationInput && (
        <div data-testid="text-details-section">
          <div
            className="text-base font-normal uppercase"
            data-testid="text-details-label"
          >
            {t('occupation.occupationDetails.title')}
          </div>
          <TextField<OccupationFormValues>
            name="occupationDetails"
            placeholder={t('occupation.occupationDetails.placeholder')}
            testId="text-details-input"
            required
            autoFocus
          />
        </div>
      )}
    </>
  )
})
